import {MeshBounds} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshBounds';
import {MeshCopiedCell} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCopiedCell';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {CopyPasteStateId, SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {
    custom,
    deserialize,
    object,
    optional,
    primitive,
    serializable,
    serialize
} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import {observable} from 'mobx';

export type CopiedCellOffsetsKey = string;

const mapModelSchema = custom(
    function(currentPropertyValue:Map<CopiedCellOffsetsKey, MeshCopiedCell>)
    {
        const result = {};

        currentPropertyValue.forEach((value, key) =>
        {
            result[key] = serialize(value);
        });

        return result;
    },
    function(jsonValue:any, context?, currentPropertyValue?)
    {
        const result = new Map<CopiedCellOffsetsKey, MeshCopiedCell>();

        Object.entries(jsonValue).forEach(([key, value]) =>
        {
            result.set(key, deserialize(MeshCopiedCell, value));
        });

        return result;
    },
);

export class CopyPasteState
{
    @observable
    @serializable
    public readonly id:CopyPasteStateId;

    @observable
    @serializable
    public readonly copiedSheetId:SheetId;

    @observable
    @serializable(object(MeshRange))
    public readonly copiedRange:MeshRange;

    @observable
    @serializable(mapModelSchema)
    public readonly copiedRangeCells:Map<CopiedCellOffsetsKey, MeshCopiedCell>;

    @observable
    @serializable
    public readonly copiedIsCut:boolean;

    @observable
    @serializable
    public isCopiedRangeVisible:boolean;

    @observable
    @serializable(optional(primitive()))
    public isStale:boolean;

    constructor(copiedSheetId:SheetId,
                copiedRange:MeshRange,
                copiedRangeCells:Map<CopiedCellOffsetsKey, MeshCopiedCell>,
                copiedIsCut:boolean,
                isCopiedRangeVisible:boolean = true,
                isStale:boolean = false)
    {
        this.copiedSheetId = copiedSheetId;
        this.copiedRange = copiedRange;
        this.copiedRangeCells = copiedRangeCells;
        this.copiedIsCut = copiedIsCut;
        this.isCopiedRangeVisible = isCopiedRangeVisible;
        this.isStale = isStale;
    }

    public getCopiedRangeBounds(meshState:MeshState):MeshBounds
    {
        if( !meshState || !this.copiedRange )
            return null;

        return MeshUtils.getRangeBounds(this.copiedRange.from, this.copiedRange.to, meshState);
    }

    public static makeCopiedCellOffsetsKey(colOffset:number, rowOffset:number):CopiedCellOffsetsKey
    {
        return `${colOffset}_${rowOffset}`;
    }
}
